import {isNil} from 'lodash';
import {
  ACH_REDEMPTION,
  ADDRESS_STEP,
  BANK_NOT_LINKED,
  BANK_STEP,
  EXISTING_AUTO_EXCHANGE,
  EXISTING_AUTO_EXCHANGE_SELL,
  EXISTING_AUTO_SELL,
  PENALTY_WITHHOLDING,
  PENDING,
  PENDING_BANK_ASSOCIATION,
  PRODUCT_TYPE_ENUM_MAP,
  PURCHASE_BLOCKED,
  REINVEST_STEP,
  RETURN_MAIL,
  RETURN_MAIL_VERIFY_ADDRESS,
  STOP_EXCHANGE,
  STOP_REDEMPTION,
  STOP_TRANSFER,
  TEMPORARILY_STOP_TRANSFER,
  UNAVAILABLE_FOR_REDEMPTION_ONLINE,
  UNAVAILABLE_ONLINE,
} from '../settings/settings';

export const sortingFn = (a, b) => {
  const temp1 = a.fundName.toLowerCase();
  const temp2 = b.fundName.toLowerCase();
  if (temp1 < temp2) {
    return -1;
  }
  if (temp1 > temp2) return 1;
  return 0;
};

export const checkFoundFeaturesByCode = (fund, serviceId) => {
  if (isNil(fund.fundLock)) {
    return false;
  }
  const {
    fundLock: { fundLocked, foundFeatures },
  } = fund;

  return (
    fundLocked &&
    foundFeatures &&
    foundFeatures.find((foundFeature) => foundFeature.serviceID === serviceId)
  );
};

export const checkMissingFeaturesByCode = (fund, serviceId) => {
  if (isNil(fund.fundLock)) {
    return false;
  }
  const {
    fundLock: { fundLocked, missingRequiredFeatures },
  } = fund;

  return (
    fundLocked &&
    missingRequiredFeatures &&
    missingRequiredFeatures.find(
      (missingRequiredFeature) => missingRequiredFeature.serviceID === serviceId
    )
  );
};

export const updateLockedList = (
  fund,
  correctBankID,
  destination,
  pendingStatusAgreements
) => {
  const selectedBankNotAssociated =
    correctBankID?.indexOf(fund.uniqueKey) === -1;
  const purchaseBlocked = fund.purchaseBlocked;

  const check = checkFoundFeaturesByCode(fund, RETURN_MAIL_VERIFY_ADDRESS);
  const stopTransfer = checkFoundFeaturesByCode(fund, STOP_TRANSFER);
  const penaltyWithholding = checkFoundFeaturesByCode(
    fund,
    PENALTY_WITHHOLDING
  );
  const stopRedemption = checkFoundFeaturesByCode(fund, STOP_REDEMPTION);
  const stopExchange = checkFoundFeaturesByCode(fund, STOP_EXCHANGE);
  const achRedemption = checkMissingFeaturesByCode(fund, ACH_REDEMPTION);
  const restrictedByAddressChange = fund.restrictedByAddressChange;
  const onlineTransaction = checkMissingFeaturesByCode(
    fund,
    UNAVAILABLE_FOR_REDEMPTION_ONLINE
  );
  const hasAutoExchange = fund.hasAutoExchange && !fund.hasAutoSell;
  const hasAutoSell = fund.hasAutoSell && !fund.hasAutoExchange;
  const bothAutoSellExchange = fund.hasAutoSell && fund.hasAutoExchange;
  const isPendingBankAssociation = pendingStatusAgreements.some(
    (agreement) => agreement === fund.uniqueKey
  );

  const boolList = [
    purchaseBlocked,
    check,
    stopTransfer,
    restrictedByAddressChange && destination === ADDRESS_STEP,
    onlineTransaction && destination !== REINVEST_STEP,
    selectedBankNotAssociated && destination === BANK_STEP,
    hasAutoExchange,
    hasAutoSell,
    bothAutoSellExchange,
    penaltyWithholding,
    stopRedemption,
    achRedemption && destination === BANK_STEP,
    stopExchange && destination !== REINVEST_STEP,
  ];
  let count = 0;
  boolList.forEach((bool) => {
    if (bool) count++;
  });

  if (isPendingBankAssociation) {
    fund.lockedText = PENDING_BANK_ASSOCIATION;
  } else if (count > 1) {
    fund.lockedText = PURCHASE_BLOCKED;
  } else {
    if (stopTransfer) {
      fund.lockedText = TEMPORARILY_STOP_TRANSFER;
    } else if (onlineTransaction && destination !== REINVEST_STEP) {
      fund.lockedText = UNAVAILABLE_ONLINE;
    } else if (selectedBankNotAssociated && destination === BANK_STEP) {
      fund.lockedText = BANK_NOT_LINKED;
    } else if (purchaseBlocked) {
      fund.lockedText = PURCHASE_BLOCKED;
    } else if (stopExchange && destination !== REINVEST_STEP) {
      fund.lockedText = PURCHASE_BLOCKED;
    } else if (penaltyWithholding) {
      fund.lockedText = PURCHASE_BLOCKED;
    } else if (stopRedemption) {
      fund.lockedText = PURCHASE_BLOCKED;
    } else if (achRedemption && destination === BANK_STEP) {
      fund.lockedText = PURCHASE_BLOCKED;
    } else if (check) {
      fund.lockedText = RETURN_MAIL;
    } else if (restrictedByAddressChange && destination === ADDRESS_STEP) {
      fund.lockedText = PENDING;
    } else if (hasAutoExchange) {
      fund.lockedText = EXISTING_AUTO_EXCHANGE;
    } else if (hasAutoSell) {
      fund.lockedText = EXISTING_AUTO_SELL;
    } else if (bothAutoSellExchange) {
      fund.lockedText = EXISTING_AUTO_EXCHANGE_SELL;
    }
  }
  return fund;
};

export const updateNonRetirementLockedList = (list, element) => {
  const purchaseBlocked = element.purchaseBlocked;
  const terminated = element?.terminated;
  const check = checkFoundFeaturesByCode(element, RETURN_MAIL_VERIFY_ADDRESS);
  const boolList = [purchaseBlocked, check];
  let count = 0;
  boolList.forEach((bool) => {
    if (bool) count++;
  });
  if (count > 1) {
    element.lockedText = PURCHASE_BLOCKED;
    list.push(element);
  } else {
    if (purchaseBlocked) {
      element.lockedText = PURCHASE_BLOCKED;
      list.push(element);
    } else if (check) {
      element.lockedText = RETURN_MAIL;
      list.push(element);
    } else if (terminated) {
      element.lockedText = STOP_TRANSFER;
      list.push(element);
    }
  }
  return list;
};

export const checkForNonRetirementUnLockFunds = (element) => {
  const purchaseBlocked = element.purchaseBlocked;
  const check = checkFoundFeaturesByCode(element, RETURN_MAIL_VERIFY_ADDRESS);
  const terminated = element?.terminated;
  const boolList = [purchaseBlocked, check, terminated];
  let count = 0;
  boolList.forEach((bool) => {
    if (bool) count++;
  });
  if (count > 0) return false;
  return true;
};

export const checkForWithholdingState = (states, fundsList) => {
  const uniqueStates = fundsList
    .map((fund) => fund.address.state)
    .filter((v, i, a) => a.indexOf(v) === i);
  if (uniqueStates.length === 1) {
    return {
      stateCode: `${uniqueStates[0]}`,
      stateName: states[uniqueStates[0]]?.stateName || `${uniqueStates[0]}`,
    };
  }
  return null;
};

export const checkIfMultiState = (fundList) => {
  const stateCodeList = fundList.map((fund) => fund.address.state);
  const singleStateCodeCheck = stateCodeList.every(
    (value, index, arr) => value === arr[0]
  );
  return !singleStateCodeCheck;
};

export const multipleStatesMandatoryCheck = (
  fundList,
  statePercentLookUp,
  STATE_CODES
) => {
  const stateCodeList = fundList
    .map((fund) => fund.address.state)
    .filter((v, i, a) => a.indexOf(v) === i);
  let stateName;
  let percentage;
  let stateCode;
  const stateList = [];
  let mandatoryStateCheck = false;
  let count = 0;
  stateCodeList.forEach((value) => {
    if (statePercentLookUp[value]?.mandatory) {
      stateCode = value;
      stateName = STATE_CODES[value].stateName;
      percentage = statePercentLookUp[value].percent;
      stateList.push({
        stateCode: stateCode,
        percentage: percentage,
        stateName: stateName,
      });
      count++;
    }
  });
  if (count > 0) {
    mandatoryStateCheck = true;
  }
  return { mandatoryStateCheck, stateList };
};

export const multiHoldingState = (
  fundList,
  statePercentLookUp,
  STATE_CODES
) => {
  if (checkIfMultiState(fundList)) {
    const { mandatoryStateCheck, stateList } = multipleStatesMandatoryCheck(
      fundList,
      statePercentLookUp,
      STATE_CODES
    );
    return {
      multiState: true,
      mandatory: mandatoryStateCheck,
      stateList,
    };
  }
  return {
    multiState: false,
  };
};

export function fundToMutualFundAgreementKey(toFund) {
  const {
    agreementID: toFundAgreementID,
    agreementNumber: toFundAgreementNumber = '',
    investmentVehicleKey: { trpcode: investmentVehicleKeyTrpCode } = {},
    productType: toFundProductType = 'MutualFund',
    newFund: toFundIsNewFund,
    trpCode,
  } = toFund;

  const toFundTrpCode = trpCode || investmentVehicleKeyTrpCode || '';

  // when we receive agreement number we get it with checksum together like
  //    5001400379-7
  // but search services are passthrough that need it split to two fields
  /** @type {string[]} */
  const agreementNumberParts = toFundAgreementNumber.split('-');
  return {
    '@Type': 'MutualFundAgreementKey',
    TRPCode: toFundTrpCode,
    // Existing fund
    checkSum:
      agreementNumberParts.length > 1 ? parseInt(agreementNumberParts[1]) : 0,
    ...{
      agreementID: toFundAgreementID,
      productType: {
        enumerationID: PRODUCT_TYPE_ENUM_MAP[toFundProductType],
        enumerationName: toFundProductType,
      },
    },
    ...(!toFundIsNewFund && {
      agreementNumber:
        agreementNumberParts.length > 0 ? agreementNumberParts[0] : '',
    }),
    ...(toFundIsNewFund && {
      agreementID: `newFund-${toFundTrpCode}-${Date.now()}`,
    }),
  };
}

export function agreementTypeToDisplay(agreementType) {
  return (agreementType === typeof agreementType) === 'string'
    ? agreementType.replace(/_/g, ' ')
    : '';
}

export const isUserCountryRestricted = (personalData) =>
  personalData &&
  personalData.primaryAddress &&
  personalData.primaryAddress.countryCode !== 'US';
